import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, EyeOff, CheckCircle, ArrowRight, GraduationCap, Users, Zap, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Register = () => {
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'guru', referral_code_used: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralStatus, setReferralStatus] = useState(null); // null | 'checking' | {valid, owner_name, bonus_coins} | 'invalid'
  const debounceRef = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await register(formData.email, formData.password, formData.name, formData.role, formData.referral_code_used);
    if (result.success) {
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const passwordStrength = (pw) => {
    if (!pw) return null;
    if (pw.length < 6) return { label: 'Terlalu pendek', color: 'bg-red-400', width: '25%' };
    if (pw.length < 8) return { label: 'Lemah', color: 'bg-orange-400', width: '50%' };
    if (pw.match(/[A-Z]/) && pw.match(/[0-9]/)) return { label: 'Kuat', color: 'bg-green-500', width: '100%' };
    return { label: 'Cukup', color: 'bg-yellow-400', width: '75%' };
  };

  const strength = passwordStrength(formData.password);

  const handleReferralChange = (val) => {
    const code = val.toUpperCase();
    setFormData(prev => ({ ...prev, referral_code_used: code }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!code) { setReferralStatus(null); return; }
    setReferralStatus('checking');
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/referral/validate/${code}`);
        setReferralStatus(data);
      } catch {
        setReferralStatus('invalid');
      }
    }, 600);
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <Helmet>
        <title>Daftar Gratis — RencanaGuru.com</title>
        <meta name="description" content="Daftar gratis dan dapatkan 10 Koin Guru untuk mulai membuat Modul Ajar, KKTP, Promes, Prota dengan AI sesuai Kurikulum Merdeka." />
        <link rel="canonical" href="https://rencanaguru.com/register" />
      </Helmet>

      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #1E3A8A 0%, #1D4ED8 60%, #3B82F6 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, #FCD34D 0%, transparent 50%), radial-gradient(circle at 80% 20%, #FFFFFF 0%, transparent 50%)' }} />

        {/* Logo */}
        <div className="relative flex items-center gap-4">
          <img src="/logo-white.png" alt="RencanaGuru" className="h-16 w-16 object-contain drop-shadow-lg" />
          <div>
            <div className="font-bold text-2xl text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              RencanaGuru<span className="text-yellow-400">.com</span>
            </div>
            <div className="text-blue-300 text-xs font-medium">Platform AI untuk Guru Indonesia</div>
          </div>
        </div>

        {/* Middle */}
        <div className="relative flex flex-col gap-8">
          <div>
            <h2 className="text-4xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Bergabung dengan<br />10.000+ Guru Indonesia
            </h2>
            <p className="text-blue-200 text-lg leading-relaxed">
              Daftar gratis dan langsung dapat <strong>10 Coin Guru</strong> untuk mulai membuat dokumen pembelajaran.
            </p>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 gap-4">
            {[
              { icon: Zap, title: '10 Coin Guru Gratis', desc: 'Dapat +5 koin ekstra jika pakai kode referral' },
              { icon: GraduationCap, title: 'Kurikulum Merdeka', desc: 'Sesuai standar Kemendikbudristek' },
              { icon: Users, title: 'Bayar Saat Butuh', desc: 'Tidak ada langganan, koin tidak expired' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-center gap-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4">
                <div className="w-10 h-10 rounded-lg bg-yellow-400 flex items-center justify-center flex-shrink-0">
                  <Icon size={20} className="text-yellow-900" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{title}</div>
                  <div className="text-blue-200 text-xs">{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: '10rb+', label: 'Guru Aktif' },
              { value: '4.9★', label: 'Rating' },
              { value: '80%', label: 'Hemat Waktu' },
            ].map(({ value, label }) => (
              <div key={label} className="text-center bg-white/10 rounded-xl py-3">
                <div className="text-yellow-400 font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
                <div className="text-blue-200 text-xs">{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-blue-300 text-xs">© 2026 RencanaGuru.com</div>
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12"
        style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #F0FDF4 100%)' }}>
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center gap-2 mb-8">
            <img src="/logo.png" alt="RencanaGuru" className="h-16 w-16 object-contain" />
            <span className="font-bold text-xl text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              RencanaGuru<span className="text-yellow-500">.com</span>
            </span>
            <span className="text-slate-400 text-xs">Platform AI untuk Guru Indonesia</span>
          </div>

          <div className="bg-white/80 backdrop-blur-sm border border-blue-100 rounded-2xl shadow-lg p-8">
            <div className="mb-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full mb-3">
                🎁 Daftar sekarang — dapat 10 Coin Guru gratis!
              </div>
              <h1 className="text-2xl font-bold text-blue-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Buat Akun Gratis
              </h1>
              <p className="text-slate-500 text-sm">Sudah punya akun?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold">Masuk di sini</Link>
              </p>
            </div>

            {error && (
              <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-start gap-2" data-testid="error-message">
                <span className="text-red-500 mt-0.5">⚠</span>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="name" className="text-slate-700 font-semibold text-sm">Nama Lengkap</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="name-input"
                  className="rounded-xl border-blue-100 bg-white focus:border-blue-500 focus:ring-blue-500 text-base h-11"
                  placeholder="Nama lengkap Anda"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="email" className="text-slate-700 font-semibold text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                  className="rounded-xl border-blue-100 bg-white focus:border-blue-500 focus:ring-blue-500 text-base h-11"
                  placeholder="nama@email.com"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="password" className="text-slate-700 font-semibold text-sm">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="password-input"
                    className="rounded-xl border-blue-100 bg-white focus:border-blue-500 focus:ring-blue-500 text-base h-11 pr-11"
                    placeholder="Minimal 8 karakter"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {/* Password strength */}
                {strength && (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${strength.color}`} style={{ width: strength.width }} />
                    </div>
                    <span className="text-xs text-slate-500">{strength.label}</span>
                  </div>
                )}
              </div>


              {/* Kode Referral (opsional) */}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="referral_code" className="text-slate-700 font-semibold text-sm">
                  Kode Referral <span className="text-slate-400 font-normal">(opsional)</span>
                </Label>
                <div className="relative">
                  <Input
                    id="referral_code"
                    type="text"
                    placeholder="Contoh: GURU-ABC123"
                    value={formData.referral_code_used}
                    onChange={(e) => handleReferralChange(e.target.value)}
                    className={`rounded-xl h-11 uppercase tracking-widest pr-10 bg-white transition-colors ${
                      referralStatus === 'invalid'
                        ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
                        : referralStatus?.valid
                        ? 'border-green-400 focus:border-green-500 focus:ring-green-200'
                        : 'border-blue-100 focus:border-blue-500'
                    }`}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {referralStatus === 'checking' && (
                      <Loader2 size={16} className="animate-spin text-slate-400" />
                    )}
                    {referralStatus?.valid && (
                      <CheckCircle size={16} className="text-green-500" />
                    )}
                    {referralStatus === 'invalid' && (
                      <XCircle size={16} className="text-red-500" />
                    )}
                  </div>
                </div>
                {referralStatus?.valid && (
                  <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                    <CheckCircle size={12} /> Kode valid! Dapat +{referralStatus.bonus_coins} Coin Guru bonus dari {referralStatus.owner_name}.
                  </p>
                )}
                {referralStatus === 'invalid' && (
                  <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                    <XCircle size={12} /> Kode referral tidak ditemukan.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="register-button"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-base shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 mt-1"
                style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Memproses...
                  </>
                ) : (
                  <>
                    Daftar Gratis Sekarang
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400">atau</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Google Register */}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/google`}
              className="flex items-center justify-center gap-3 w-full border border-slate-300 rounded-xl py-3 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Daftar dengan Google
            </a>

            <div className="mt-4 pt-4 border-t border-blue-100 text-center">
              <p className="text-xs text-slate-400">
                Dengan mendaftar, Anda menyetujui{' '}
                <Link to="/terms-of-service" className="text-blue-600 hover:underline">Syarat & Ketentuan</Link>
                {' '}dan{' '}
                <Link to="/privacy-policy" className="text-blue-600 hover:underline">Kebijakan Privasi</Link>
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            © 2026 RencanaGuru.com · Platform AI untuk Guru Indonesia
          </p>
        </div>
      </div>
    </div>
  );
};
